import {fonts, colors, tokens} from '@commercial-helios/ui/theme'
import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import FeatureBlock from '@/components/feature-block'
import Footer from '@/components/footer'
import Header from '@/components/header'
import SEO from '@/components/seo'
import type {PricingPageQuery} from '@/graphql-types'
import {PricingSection} from '@/sections/pricing'
import QuoteCarousel from '@/sections/quote-carousel'
import SignupBannerSection from '@/sections/signup-banner'
import TitleWithAccordion from '@/sections/title-with-accordion'
import TitleWithIconGridSection from '@/sections/title-with-icon-grid'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import Image from '@helios/shared/components/image'
import RichText, {hasRichText} from '@helios/shared/components/rich-text'
import {Main} from '@helios/shared/components/skip-to-content'
import {Table} from '@helios/shared/components/table'
import locales from '@helios/shared/locale/locales'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {Section, BackgroundWrapper, Layout} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'
import getEnvironmentFormId from '@helios/shared/utils/get-environment-field'
import mq from '@helios/shared/utils/media-query'

type Props = PageProps<PricingPageQuery>

const dataTestId = 'pricing-page'

const PricingPage = (props: Props) => {
    const data = props.data.prismicPricingPage.data
    const globalStrings = useGlobalStrings()

    if (!data) {
        return handleNoPrismicData()
    }

    return (
        <>
            <Header />

            <Main>
                {hasRichText(data.title.richText) && (
                    <TitleSection data-testid={`${dataTestId}-title-section`}>
                        <RichText as="h1" content={data.title.richText} variant="heading-large" />
                        {data.title_image && (
                            <StyledImage
                                image={data.title_image}
                                data-testid="pricing-hero-image"
                            />
                        )}
                    </TitleSection>
                )}

                <PricingSection dataTestId={`${dataTestId}-cards`} withTitleImage={false} />

                {!!data.tables.filter(({table}) => !!table).length && (
                    <BackgroundWrapper color="muted">
                        <TablesWrapper data-testid={`${dataTestId}-table`}>
                            <TitleWrapper variant="two-asymmetrical">
                                <Text variant="heading-base">{data.tables_title}</Text>
                            </TitleWrapper>

                            {data.tables.map(({table}) => {
                                if (!table.document) {
                                    return null
                                }

                                const {
                                    category,
                                    essential_heading,
                                    premium_heading,
                                    pro_heading,
                                    rows,
                                } = table.document.data

                                const headColumns = [
                                    category,
                                    essential_heading,
                                    pro_heading,
                                    premium_heading,
                                ]
                                const bodyHeads = rows.map((el) => el.feature)

                                const bodyHeadsTooltips = rows.map((el) => el.feature_tooltip || '')

                                const bodyColumns = rows.map((el) => [
                                    el.essential_text || el.essential,
                                    el.pro_text || el.pro,
                                    el.premium_text || el.premium,
                                ])

                                return (
                                    <StyledTable
                                        key={table.document.id}
                                        headColumns={headColumns}
                                        bodyHeads={bodyHeads}
                                        bodyHeadsTooltips={bodyHeadsTooltips}
                                        bodyColumns={bodyColumns}
                                        tickAltText={data.tables_tick_screen_reader_alt}
                                        disclaimer={globalStrings.financial_partner_disclaimer}
                                    />
                                )
                            })}
                        </TablesWrapper>
                    </BackgroundWrapper>
                )}

                {!!data.questions_answers.length && (
                    <TitleWithAccordion
                        dataTestId={dataTestId}
                        title={data.questions_title}
                        items={data.questions_answers?.map(({question, answer}) => ({
                            title: question,
                            body: answer?.richText,
                        }))}
                    />
                )}

                {data.testimonial.document && (
                    <QuoteCarousel
                        dataTestId={`${dataTestId}-quotes`}
                        items={[
                            {
                                image: data.testimonial.document.data.image,
                                quote: data.testimonial.document.data.quote,
                                mirrored: true,
                                attribution: {
                                    name: data.testimonial.document.data.attribution_name,
                                    title: data.testimonial.document.data.attribution_title,
                                },
                            },
                        ]}
                    />
                )}

                {data.social_proof_ratings_section.document && (
                    <TitleWithIconGridSection
                        dataTestId={`${dataTestId}-ratings`}
                        titleVariant="heading-large"
                        highlightColor="pink"
                        title={data.social_proof_ratings_section.document.data.title.richText}
                        items={data.social_proof_ratings_section.document.data.ratings.map(
                            ({rating}) => ({
                                icon: rating.document?.data.logo,
                                stars: rating.document?.data.rating_number,
                                link: rating.document?.data.link,
                            })
                        )}
                    />
                )}

                {data.social_proof_logos_extended_section.document && (
                    <TitleWithIconGridSection
                        dataTestId={`${dataTestId}-logos`}
                        titleVariant="heading-large"
                        highlightColor="yellow"
                        title={
                            data.social_proof_logos_extended_section.document.data.title.richText
                        }
                        items={data.social_proof_logos_extended_section.document.data.logos.map(
                            ({logo}) => ({
                                icon: logo,
                            })
                        )}
                    />
                )}

                {data.why_pleo.map((block, i) => (
                    <Section key={`${block.title}-${i}`} data-testid={`${dataTestId}-why-pleo`}>
                        <FeatureBlock
                            {...{...block, body: block.body.richText}}
                            preTitle={block.pre_title}
                        />
                    </Section>
                ))}

                {data.signup_banner.document && (
                    <SignupBannerSection
                        dataTestId={`${dataTestId}-signup-banner`}
                        {...data.signup_banner.document.data}
                        color="pink"
                        formId={getEnvironmentFormId(
                            data.signup_banner.document.data.hubspot_form_id_production,
                            data.signup_banner.document.data.hubspot_form_id_staging
                        )}
                        placeholder={data.signup_banner.document.data.email_input_placeholder}
                        submitText={data.signup_banner.document.data.submit_text}
                        errorText={data.signup_banner.document.data.email_input_error_text}
                    />
                )}
            </Main>

            <Footer />
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<PricingPageQuery>) => {
    const pageData = data.prismicPricingPage.data

    // Can't access React Context here (until gatsby@5.6.0), so useLocale() doesn't work
    const locale = locales.find((locale) => {
        return locale.code === pageContext.locale
    })

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
            preventIndexing={pageData.exclude_robots}
            canonicalLocation={`/${locale?.urlCode}/pricing`}
        />
    )
}

const TablesWrapper = styled(Section)`
    display: grid;
    grid-gap: ${tokens.spacing12};

    ${mq.min('sm')} {
        grid-gap: ${tokens.spacing20};
    }
`

const StyledTable = styled(Table)`
    padding: ${tokens.spacing20} ${tokens.spacing16};
    background: ${colors.background.default};

    ${mq.min('sm')} {
        padding: ${tokens.spacing40};
    }
`

const TitleSection = styled(Section)`
    ${mq.min('sm')} {
        text-align: center;
    }

    ${mq.min('md')} {
        padding-top: 80px;
        padding-bottom: 80px;
    }

    strong {
        font-weight: ${fonts.weights.normal};
        background: ${colors.background.pink};
    }
`

const StyledImage = styled(Image)`
    margin-top: ${tokens.spacing10};
    margin-right: auto;
    margin-left: auto;
`

const TitleWrapper = styled(Layout)`
    margin-bottom: ${tokens.spacing8};

    ${mq.min('sm')} {
        margin-bottom: ${tokens.spacing20};
    }
`

export const query = graphql`
    query PricingPage($locale: String!, $uid: String!) {
        prismicPricingPage(uid: {eq: $uid}, lang: {eq: $locale}) {
            _previewable
            data {
                meta_title
                meta_description
                exclude_robots
                open_graph_image {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                title_image {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                title {
                    richText
                }
                title_image {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                tables_title
                tables_tick_screen_reader_alt
                tables {
                    table {
                        document {
                            ... on PrismicPricingTable {
                                id
                                data {
                                    category
                                    essential_heading
                                    pro_heading
                                    premium_heading
                                    rows {
                                        feature
                                        feature_tooltip
                                        essential_text
                                        essential
                                        pro_text
                                        pro
                                        premium_text
                                        premium
                                    }
                                }
                            }
                        }
                    }
                }
                questions_title
                questions_answers {
                    question
                    answer {
                        richText
                    }
                }
                testimonial {
                    document {
                        ... on PrismicTestimonial {
                            id
                            data {
                                image {
                                    alt
                                    copyright
                                    url
                                    dimensions {
                                        height
                                        width
                                    }
                                }
                                quote
                                attribution_name
                                attribution_title
                                cta_text
                            }
                        }
                    }
                }
                social_proof_logos_extended_section {
                    document {
                        ... on PrismicSocialProofLogosExtendedSection {
                            data {
                                title {
                                    richText
                                }
                                logos {
                                    logo {
                                        alt
                                        copyright
                                        url
                                        dimensions {
                                            height
                                            width
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                social_proof_ratings_section {
                    document {
                        ... on PrismicSocialProofRatingsSection {
                            data {
                                title {
                                    richText
                                }
                                ratings {
                                    rating {
                                        document {
                                            ... on PrismicRating {
                                                data {
                                                    logo {
                                                        alt
                                                        copyright
                                                        url
                                                        dimensions {
                                                            height
                                                            width
                                                        }
                                                    }
                                                    stars
                                                    rating_number
                                                    link
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                why_pleo {
                    pre_title
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    title
                    body {
                        richText
                    }
                }
                signup_banner {
                    document {
                        ...SignupBanner
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(PricingPage)
